import { useState } from 'react';
import { Loader2, Users2 } from 'lucide-react';
import { Button } from '../../ui/button';
import api from '../../../services/api';
import { toast } from 'sonner';

/**
 * One-click repair for the "player on a team but Status still says pending"
 * drift. Walks every team in the tournament and promotes any registration
 * whose player_id is on a team's roster from `pending` (or `not_selected`)
 * to `selected`. Idempotent — safe to re-run. Never touches `confirmed` or
 * `cancelled` rows; never changes payment_status.
 */
const RepairTeamStatusesButton = ({ tournamentId, onRepaired }) => {
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/tournaments/${tournamentId}/registrations/repair-team-statuses`,
      );
      const repaired = data?.registrations_repaired || 0;
      const teams = data?.teams_examined || 0;
      if (repaired > 0) {
        const names = (data?.before || [])
          .map((r) => r.player_name)
          .filter(Boolean)
          .slice(0, 5)
          .join(', ');
        toast.success(
          `Promoted ${repaired} registration${repaired === 1 ? '' : 's'} to "selected"${names ? ` — ${names}${(data.before || []).length > 5 ? '…' : ''}` : ''}.`,
        );
      } else {
        toast.info(
          `All on-team players already have a correct status — nothing to repair (examined ${teams} team${teams === 1 ? '' : 's'}).`,
        );
      }
      onRepaired?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to repair team statuses');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      className="text-violet-700 border-violet-300 hover:bg-violet-50"
      onClick={handleClick}
      disabled={busy}
      title="Promote any pending or not_selected registration whose player is on a team to 'selected'. Fixes the drift where a roster shows the player but the Status column still says pending."
      data-testid="repair-team-statuses-btn"
    >
      {busy ? (
        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
      ) : (
        <Users2 className="h-4 w-4 mr-1" />
      )}
      Repair team statuses
    </Button>
  );
};

export default RepairTeamStatusesButton;
